/**
 * Patch Selector
 * ----------------------------------------------------------------------
 * Configurateur multi-pays : toggle Brodé/PVC, recherche instantanée,
 * liste alphabétique avec quantité par pays, récapitulatif et ajout
 * groupé au panier.
 *
 * SOURCE DE DONNÉES :
 * Les pays viennent du <script type="application/json" data-patch-selector-data>
 * injecté par blocks/patch-selector.liquid, généré depuis les vrais
 * produits Shopify (nom, image, prix, disponibilité). Une variante
 * absente ou indisponible s'affiche comme rupture de stock.
 *
 * PANIER :
 * L'ajout au panier utilise le vrai Cart AJAX API Shopify
 * (Theme.routes.cart_add_url = POST /cart/add.js) et diffuse un vrai
 * CartLinesUpdateEvent (@shopify/events, chargé globalement par Horizon
 * via l'import map de snippets/scripts.liquid) — c'est le même événement
 * que product-form.js utilise nativement, donc le compteur panier du
 * header et l'ouverture automatique du drawer (cart-drawer.js) suivent
 * sans code supplémentaire de notre part.
 */

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class PatchSelector {
  constructor(rootElement) {
    this.root = rootElement;
    this.sectionId = this.root.dataset.sectionId;
    this.currentHandle = this.root.dataset.currentHandle || null;
    this.currentType = this.root.dataset.currentType || null;

    this.elements = {
      typeToggle: this.root.querySelector('[data-type-btn]')?.closest('.patch-selector__type-toggle'),
      typeButtons: Array.from(this.root.querySelectorAll('[data-type-btn]')),
      searchInput: this.root.querySelector('.patch-selector__search-input'),
      listContainer: this.root.querySelector('[data-country-list]'),
      emptyState: this.root.querySelector('[data-empty-state]'),
      summaryEmpty: this.root.querySelector('[data-summary-empty]'),
      summaryList: this.root.querySelector('[data-summary-list]'),
      addToCartBtn: this.root.querySelector('[data-add-to-cart]'),
      addToCartError: this.root.querySelector('[data-add-to-cart-error]'),
      dataScript: this.root.querySelector('[data-patch-selector-data]'),
    };

    this.countries = this.parseData();

    // state.selections : Map<"type:handle", quantity>
    this.state = {
      type: 'brode',
      query: '',
      selections: new Map(),
    };

    this.priceFormatter = new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    });

    this.defaultAddToCartLabel = this.elements.addToCartBtn
      ? this.elements.addToCartBtn.textContent.trim()
      : 'Ajouter tous les patchs au panier';

    this.init();
  }

  init() {
    if (!this.countries.length) return;

    this.bindEvents();
    this.applyCurrentProductPreselection();
    this.renderList();
    this.renderSummary();
  }

  // -- Présélection du produit couramment affiché sur la fiche produit ---

  applyCurrentProductPreselection() {
    if (!this.currentHandle) return;

    const country = this.countries.find((item) => item.handle === this.currentHandle);
    if (!country) return;

    if (this.currentType && this.getVariant(country, this.currentType)) {
      this.state.type = this.currentType;
      this.elements.typeButtons.forEach((btn) => {
        const isActive = btn.dataset.typeBtn === this.currentType;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-checked', String(isActive));
      });
    }

    const variant = this.getVariant(country, this.state.type);
    if (variant && variant.available) {
      this.state.selections.set(this.selectionKey(this.state.type, country.handle), 1);
    }
  }

  parseData() {
    if (!this.elements.dataScript) return [];

    let entries;
    try {
      entries = JSON.parse(this.elements.dataScript.textContent);
    } catch (error) {
      console.error('[Patch Selector] Données pays invalides :', error);
      return [];
    }

    // Liste plate (1 entrée par produit brodé/PVC) -> regroupement par pays.
    const countries = new Map();

    entries.forEach((entry) => {
      if (!countries.has(entry.slug)) {
        countries.set(entry.slug, {
          handle: entry.slug,
          name: entry.name,
          flag: entry.flag,
          variants: { brode: null, pvc: null },
        });
      }

      const country = countries.get(entry.slug);
      country.variants[entry.type] = {
        variantId: entry.variantId,
        price: entry.price,
        available: entry.available,
      };

      // Le produit "brodé" est prioritaire pour le nom/l'image affichés.
      if (entry.type === 'brode') {
        country.name = entry.name;
        country.flag = entry.flag;
      }
    });

    return Array.from(countries.values());
  }

  bindEvents() {
    this.elements.typeButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.setType(btn.dataset.typeBtn));
    });

    if (this.elements.searchInput) {
      this.elements.searchInput.addEventListener('input', (event) => {
        this.state.query = event.target.value;
        this.renderList();
      });
    }

    if (this.elements.listContainer) {
      this.elements.listContainer.addEventListener('click', (event) => this.handleListClick(event));
    }

    if (this.elements.summaryList) {
      this.elements.summaryList.addEventListener('click', (event) => this.handleSummaryClick(event));
    }

    if (this.elements.addToCartBtn) {
      this.elements.addToCartBtn.addEventListener('click', () => this.addToCart());
    }
  }

  // -- State helpers -------------------------------------------------------

  setType(type) {
    if (type === this.state.type) return;
    this.state.type = type;

    this.elements.typeButtons.forEach((btn) => {
      const isActive = btn.dataset.typeBtn === type;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-checked', String(isActive));
    });

    this.renderList();
  }

  displayName(country, type) {
    return `${country.name} ${type === 'pvc' ? 'PVC' : 'brodé'}`;
  }

  selectionKey(type, handle) {
    return `${type}:${handle}`;
  }

  getQuantity(type, handle) {
    return this.state.selections.get(this.selectionKey(type, handle)) || 0;
  }

  setQuantity(type, handle, quantity) {
    const key = this.selectionKey(type, handle);
    if (quantity <= 0) {
      this.state.selections.delete(key);
    } else {
      this.state.selections.set(key, quantity);
    }
    this.renderList();
    this.renderSummary();
  }

  // -- Data helpers ----------------------------------------------------

  normalize(value) {
    return value
      .toLowerCase()
      .normalize('NFKD')
      .replace(/\p{Diacritic}/gu, '');
  }

  getVariant(country, type) {
    return country.variants[type];
  }

  filteredSortedCountries() {
    const query = this.normalize(this.state.query.trim());

    return this.countries
      .filter((country) => !query || this.normalize(country.name).includes(query))
      .sort((a, b) => {
        if (this.currentHandle) {
          if (a.handle === this.currentHandle) return -1;
          if (b.handle === this.currentHandle) return 1;
        }
        return a.name.localeCompare(b.name, 'fr');
      });
  }

  // -- Rendering: country list -----------------------------------------

  renderList() {
    const { listContainer, emptyState } = this.elements;
    if (!listContainer) return;

    const countries = this.filteredSortedCountries();
    listContainer.innerHTML = '';

    if (emptyState) {
      emptyState.hidden = countries.length > 0;
    }

    countries.forEach((country) => {
      listContainer.appendChild(this.buildCountryRow(country));
    });
  }

  buildCountryRow(country) {
    const variant = this.getVariant(country, this.state.type);
    const isUnavailable = !variant || !variant.available;
    const quantity = isUnavailable ? 0 : this.getQuantity(this.state.type, country.handle);

    const item = document.createElement('li');
    item.className = 'patch-selector__list-item';

    const row = document.createElement('div');
    row.className = 'patch-selector__country-row';
    row.classList.toggle('is-selected', quantity > 0);
    row.classList.toggle('is-unavailable', isUnavailable);
    row.dataset.handle = country.handle;

    const flag = document.createElement('img');
    flag.className = 'patch-selector__country-flag';
    flag.src = country.flag;
    flag.width = 24;
    flag.height = 18;
    flag.loading = 'lazy';
    flag.alt = '';
    flag.setAttribute('aria-hidden', 'true');

    const name = document.createElement('span');
    name.className = 'patch-selector__country-name';
    name.textContent = this.displayName(country, this.state.type);

    const control = document.createElement('div');
    control.className = 'patch-selector__control';
    control.appendChild(this.buildQuantityControl(country, variant, quantity));

    row.append(flag, name, control);
    item.appendChild(row);

    return item;
  }

  buildQuantityControl(country, variant, quantity) {
    if (!variant || !variant.available) {
      const badge = document.createElement('span');
      badge.className = 'patch-selector__out-of-stock';
      badge.textContent = 'Rupture de stock';
      return badge;
    }

    if (quantity <= 0) {
      const addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'patch-selector__country-add';
      addBtn.dataset.action = 'add';
      addBtn.dataset.handle = country.handle;
      addBtn.setAttribute('aria-label', `Ajouter ${country.name}`);
      addBtn.textContent = '+';
      return addBtn;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'patch-selector__qty';

    const decrementBtn = document.createElement('button');
    decrementBtn.type = 'button';
    decrementBtn.className = 'patch-selector__qty-btn';
    decrementBtn.dataset.action = 'decrement';
    decrementBtn.dataset.handle = country.handle;
    decrementBtn.setAttribute('aria-label', `Retirer un patch ${country.name}`);
    decrementBtn.textContent = '−';

    const value = document.createElement('span');
    value.className = 'patch-selector__qty-value';
    value.textContent = String(quantity);

    const incrementBtn = document.createElement('button');
    incrementBtn.type = 'button';
    incrementBtn.className = 'patch-selector__qty-btn';
    incrementBtn.dataset.action = 'increment';
    incrementBtn.dataset.handle = country.handle;
    incrementBtn.setAttribute('aria-label', `Ajouter un patch ${country.name}`);
    incrementBtn.textContent = '+';

    wrapper.append(decrementBtn, value, incrementBtn);
    return wrapper;
  }

  handleListClick(event) {
    const button = event.target.closest('button[data-action]');
    if (!button) return;

    const { action, handle } = button.dataset;
    const currentQuantity = this.getQuantity(this.state.type, handle);

    if (action === 'add' || action === 'increment') {
      this.setQuantity(this.state.type, handle, currentQuantity + 1);
    } else if (action === 'decrement') {
      this.setQuantity(this.state.type, handle, currentQuantity - 1);
    }
  }

  // -- Rendering: summary ------------------------------------------------

  renderSummary() {
    const { summaryEmpty, summaryList, addToCartBtn } = this.elements;
    if (!summaryList) return;

    const entries = this.getSelectionEntries();

    summaryList.innerHTML = '';

    const hasSelection = entries.length > 0;
    if (summaryEmpty) summaryEmpty.hidden = hasSelection;
    summaryList.hidden = !hasSelection;
    if (addToCartBtn) addToCartBtn.disabled = !hasSelection;

    let totalQuantity = 0;
    let totalPrice = 0;

    entries.forEach(({ country, type, quantity, variant }) => {
      totalQuantity += quantity;
      totalPrice += quantity * variant.price;

      const chip = document.createElement('li');
      chip.className = 'patch-selector__summary-chip';

      const chipFlag = document.createElement('img');
      chipFlag.className = 'patch-selector__summary-chip-flag';
      chipFlag.src = country.flag;
      chipFlag.width = 18;
      chipFlag.height = 13;
      chipFlag.alt = '';
      chipFlag.setAttribute('aria-hidden', 'true');

      const label = document.createElement('span');
      const displayName = this.displayName(country, type);
      label.textContent = quantity > 1 ? `${displayName} (${quantity})` : displayName;

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'patch-selector__summary-chip-remove';
      removeBtn.dataset.handle = country.handle;
      removeBtn.dataset.type = type;
      removeBtn.setAttribute('aria-label', `Retirer ${country.name}`);
      removeBtn.textContent = '×';

      chip.append(chipFlag, label, removeBtn);
      summaryList.appendChild(chip);
    });

    this.updateAddToCartLabel(totalQuantity, totalPrice);
  }

  updateAddToCartLabel(totalQuantity, totalPrice) {
    const btn = this.elements.addToCartBtn;
    if (!btn || btn.dataset.state === 'loading') return;

    btn.textContent = totalQuantity > 0
      ? `Ajouter ${totalQuantity} patch${totalQuantity > 1 ? 's' : ''} au panier • ${this.priceFormatter.format(totalPrice)}`
      : this.defaultAddToCartLabel;
  }

  handleSummaryClick(event) {
    const removeBtn = event.target.closest('button[data-handle]');
    if (!removeBtn) return;

    this.setQuantity(removeBtn.dataset.type, removeBtn.dataset.handle, 0);
  }

  getSelectionEntries() {
    const entries = [];

    this.state.selections.forEach((quantity, key) => {
      const [type, handle] = key.split(':');
      const country = this.countries.find((item) => item.handle === handle);
      if (!country) return;

      const variant = this.getVariant(country, type);
      entries.push({ country, type, quantity, variant });
    });

    return entries;
  }

  // -- Ajout au panier (Cart AJAX API réel) -------------------------------

  async addToCart() {
    const entries = this.getSelectionEntries();
    if (!entries.length) return;

    const items = entries.map(({ variant, quantity }) => ({
      id: Number(variant.variantId),
      quantity,
    }));

    this.clearAddToCartError();
    this.setAddToCartLoading(true);

    const deferred = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        const message = result.description || result.message || 'Un ou plusieurs patchs ne sont plus disponibles.';

        document.dispatchEvent(
          new CartErrorEvent({
            error: message,
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );

        const cart = await this.fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'patch-selector', itemCount: cart.item_count },
        });

        this.showAddToCartError(message);
        return;
      }

      const cart = await this.fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'patch-selector', itemCount: cart.item_count, didError: false },
      });

      this.state.selections.clear();
      this.renderList();
      this.renderSummary();
      this.flashAddToCartSuccess();
    } catch (error) {
      deferred.reject(error);
      document.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Erreur réseau lors de l’ajout au panier.',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.showAddToCartError('Impossible d’ajouter au panier. Vérifiez votre connexion et réessayez.');
    } finally {
      this.setAddToCartLoading(false);
    }
  }

  async fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  setAddToCartLoading(isLoading) {
    const btn = this.elements.addToCartBtn;
    if (!btn) return;

    if (isLoading) {
      btn.dataset.state = 'loading';
      btn.disabled = true;
      btn.textContent = 'Ajout en cours…';
    } else {
      delete btn.dataset.state;
      btn.disabled = this.getSelectionEntries().length === 0;
    }
  }

  flashAddToCartSuccess() {
    const btn = this.elements.addToCartBtn;
    if (!btn) return;

    btn.textContent = 'Ajouté ✓';
    window.setTimeout(() => this.updateAddToCartLabel(0, 0), 1500);
  }

  showAddToCartError(message) {
    const el = this.elements.addToCartError;
    if (!el) {
      console.error('[Patch Selector]', message);
      return;
    }
    el.textContent = message;
    el.hidden = false;
  }

  clearAddToCartError() {
    const el = this.elements.addToCartError;
    if (!el) return;
    el.hidden = true;
    el.textContent = '';
  }

  destroy() {
    // Point d'entrée pour un futur nettoyage (listeners, etc.)
  }
}

/**
 * Initialise toutes les instances présentes sur la page, et gère
 * le rechargement de section dans le theme editor Shopify (OS 2.0).
 */
function initPatchSelectors(container = document) {
  container.querySelectorAll('.patch-selector').forEach((root) => {
    if (root.patchSelectorInstance) return;
    root.patchSelectorInstance = new PatchSelector(root);
  });
}

document.addEventListener('DOMContentLoaded', () => initPatchSelectors());

document.addEventListener('shopify:section:load', (event) => {
  initPatchSelectors(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  const root = event.target.querySelector('.patch-selector');
  if (root && root.patchSelectorInstance) {
    root.patchSelectorInstance.destroy();
    delete root.patchSelectorInstance;
  }
});
